// src/tenantAdmin/pages/TenantRunListPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import AdminTable from '../../admin/components/AdminTable.jsx';
import AdminPagination from '../../admin/components/AdminPagination.jsx';
import StatusBadge from '../../admin/components/StatusBadge.jsx';
import RunFilters from '../../admin/components/RunFilters.jsx';
import { listRuns } from '../../admin/runsApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';

const LIMIT = 20;

function sevenDaysAgoIso() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}

export default function TenantRunListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTenant } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const offset = parseInt(searchParams.get('offset') || '0', 10);
  const statusFromUrl = (searchParams.get('status') || '').split(',').filter(Boolean);
  const urlSince    = searchParams.get('since')           || sevenDaysAgoIso();
  const urlBefore   = searchParams.get('before')          || '';
  const urlUser     = searchParams.get('user')            || '';
  const urlPlatform = searchParams.get('platform')        || '';
  const urlFormat   = searchParams.get('content_format')  || '';

  const hasActiveFilters =
    statusFromUrl.length > 0 ||
    !!urlUser || !!urlPlatform || !!urlFormat ||
    (searchParams.get('since')  || '') !== '' ||
    (searchParams.get('before') || '') !== '';

  function patchParams(patch) {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: false });
  }

  const [runs, setRuns]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const tenantId = activeTenant?.tenant_id;

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    try {
      const data = await listRuns({
        tenant: tenantId,                                             // explicit — see plan Context
        status: statusFromUrl.length > 0 ? statusFromUrl : undefined,
        since:  urlSince  || undefined,
        before: urlBefore || undefined,
        user:   urlUser   || undefined,
        platform: urlPlatform || undefined,
        contentFormat: urlFormat || undefined,
        limit: LIMIT,
        offset,
      });
      setRuns(data.runs || []);
      setTotal(typeof data.total === 'number' ? data.total : (data.runs?.length || 0));
    } catch (err) {
      setError(err.message || 'Errore nel caricamento delle run');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, urlSince, urlBefore, urlUser, urlPlatform, urlFormat, offset, statusFromUrl.join(',')]);

  useEffect(() => { load(); }, [load]);

  if (!activeTenant) return null;

  const columns = [
    {
      key: 'topic', label: 'Topic',
      render: row => (
        <span title={row.topic || ''} style={{ display: 'inline-block', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
          {row.topic || '—'}
        </span>
      ),
    },
    {
      key: 'platform', label: 'Platform / Format', width: '180px',
      render: row => `${row.platform || '—'}${row.content_format ? ` / ${row.content_format}` : ''}`,
    },
    { key: 'status', label: 'Stato', width: '120px', render: row => <StatusBadge status={row.status} /> },
    {
      key: 'user_email', label: 'User', width: '180px',
      render: row => row.created_by_email || row.user_email || row.user_id || '—',
    },
    {
      key: 'created_at', label: 'Created', width: '140px',
      render: row => row.created_at
        ? new Date(row.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
        : '—',
    },
    {
      key: 'task_id', label: 'Task ID', width: '110px',
      render: row => row.task_id ? (
        <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>
          {String(row.task_id).slice(-8)}
        </code>
      ) : '—',
    },
  ];

  return (
    <div style={{ padding: '32px 40px', fontFamily: "'DM Sans', sans-serif", color: '#1d1d1f', maxWidth: '1200px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Storico Generazioni</h1>
        <p style={{ color: '#6c6c6d', fontSize: '13px', margin: '4px 0 0' }}>
          Cronologia delle generazioni per {activeTenant.display_name} (ultimi 7 giorni)
        </p>
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#EF4444', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <RunFilters
        status={statusFromUrl}
        since={urlSince}
        before={urlBefore}
        user={urlUser}
        platform={urlPlatform}
        contentFormat={urlFormat}
        onChange={(patch) => patchParams({ ...patch, offset: '0' })}
      />

      {hasActiveFilters && (
        <button
          type="button"
          onClick={() => setSearchParams(new URLSearchParams(), { replace: false })}
          style={{
            padding: '5px 10px', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)',
            background: '#fff', color: '#3a3a3c', fontSize: '12px', cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif", marginBottom: '12px',
          }}
        >
          Reset filtri
        </button>
      )}

      <div style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <AdminTable
          columns={columns}
          rows={runs}
          loading={loading}
          emptyMessage={hasActiveFilters ? 'Nessuna run trovata con i filtri attuali' : 'Nessuna run negli ultimi 7 giorni'}
          onRowClick={row => navigate(`${location.pathname}/${row.task_id}`, { state: { from: `${location.pathname}?${searchParams}` } })}
          getRowKey={row => row.task_id}
        />
      </div>
      <AdminPagination total={total} limit={LIMIT} offset={offset} onChange={n => patchParams({ offset: n })} itemLabel="run" />
    </div>
  );
}
